import React, {useCallback, useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import CustomInput from "../../_common/component/CustomInput";
import CustomCheckbox from "../../_common/component/CustomCheckbox";
import {FIND_ACCOUNT_OPTIONS, LOGIN_USER_INFO_INIT_SET} from "../../../constant/options";
import SubmitButton from "../../_common/component/SubmitButton";
import {useTheme} from "@emotion/react";
import SpreadItem from "../../_common/component/SpreadItem";
import {BROADCAST_ON_SUBMIT_LOGIN, useApplicationContext} from "../../../context/ApplicationContextProvider";
import {lang} from "../../../lang";
import {REGEX_EMAIL, REGEX_PASSWORD} from "../../../constant/regexs";
import {useModelContext} from "../../../context/ModelContextProvider";
import {useDispatch} from "react-redux";
import {account_actions} from "../../../redux/slice/AccountReducer";
import {useNavigate} from "react-router-dom";
import {storage_get, storage_set} from "../../../common";
import {useDialogContext} from "../../../context/DialogContextProvider";
import Log from "../../../common/Log";

const LoginForm = () => {
    const globalTheme = useTheme();
    const dispatch = useDispatch();
    const {account_model, isSuccess, isError} = useModelContext();
    const {broadcast} = useApplicationContext();
    const {alert} = useDialogContext();
    const navigate = useNavigate();

    // state
    const [is_loading, setIsLoading] = useState(false);
    const [account_error, setAccountError] = useState(false);
    const [password_error, setPasswordError] = useState(false);

    // ref
    const user_info = useRef(LOGIN_USER_INFO_INIT_SET);

    //css style
    const {view} = useComponentStyle();

    useEffect(() => {
        const unlisten = broadcast.listen([BROADCAST_ON_SUBMIT_LOGIN], ({value}) => {
            value && validateInputValue();
        });

        return () => {
            broadcast.unlisten(unlisten);
        };
    }, [broadcast]);

    // user_info 값의 초기화 (로그인 상태 유지인 keep_login은 유지)
    const initialUserInfo = useCallback(() => {
        user_info.current.account = {
            value: undefined,
            error_msg: null,
        };
        user_info.current.password = {
            value: undefined,
            error_msg: null,
        };
    }, []);

    // 페이지 재 렌더시에 id, pw값 초기화 (ex.로그아웃, 비번변경 후 로그인페이지로 navigate)
    useEffect(() => {
        initialUserInfo();
    }, [initialUserInfo]);

    const validateInputValue = () => {
        let email = user_info.current.account.value;
        let pw = user_info.current.password.value;

        // email, pw 중 하나라도 빈 값인 경우
        if (!email || !pw) {
            !email && (user_info.current.account.error_msg = lang.login.empty_login_email);
            !pw && (user_info.current.password.error_msg = lang.login.empty_login_password);
        }
        // email이 있는 경우 정규식 체크
        if (email) {
            !REGEX_EMAIL.test(email)
                ? (user_info.current.account.error_msg = lang.login.check_login_email)
                : (user_info.current.account.error_msg = null);
        }
        // pw가 있는 경우 정규식 체크
        if (pw) {
            !REGEX_PASSWORD.test(pw)
                ? (user_info.current.password.error_msg = lang.login.check_login_password)
                : (user_info.current.password.error_msg = null);
        }
        if (user_info.current.account.error_msg === null && user_info.current.password.error_msg === null) {
            setAccountError(false);
            setPasswordError(false);
            submitLogin();
        } else {
            user_info.current.account.error_msg && setAccountError(true);
            user_info.current.password.error_msg && setPasswordError(true);
        }
    };

    const submitLogin = async () => {
        setIsLoading(true);

        const post_signin_res = await account_model.postSignin(
            user_info.current.account.value,
            user_info.current.password.value
        );

        if (isSuccess(post_signin_res)) {
            const tmp = {
                info: post_signin_res.data.account,
                token: post_signin_res.data.login_token,
                with_detail_info: false,
                company: post_signin_res.data.company,
            };

            // 15일 동안 로그인 상태 유지
            if (user_info.current.keep_login) {
                storage_set("auth_info", post_signin_res.data, 1000 * 60 * 60 * 24 * 15);
            }

            let next_url = null;
            let tmp_state = null;

            // 계정 정보의 last_password_change_time 이 null이면 비밀번호 초기화되었다고 판단 -> 비밀번호 변경 페이지로 이동
            if (!post_signin_res.data.account.last_password_change_time) {
                next_url = "/account/update-password";
                tmp_state = {
                    init_pw: user_info.current.password.value,
                };
            } else {
                // next_page_info가 없으면 강의 리스트 페이지로 이동
                if (!storage_get("next_page_info")) {
                    next_url = "/course-list";
                } else {
                    next_url = storage_get("next_page_info").url;
                }
            }

            dispatch(account_actions.doLogin(tmp));

            tmp_state ? navigate(next_url, {state: tmp_state}) : navigate(next_url);
        } else if (isError(post_signin_res)) {
            if (post_signin_res.code === 4500) {
                await alert(lang.login.login_failed);

                initialUserInfo();
            } else {
                alert(lang.request.request_error);
            }
            Log.private("postSignIn 에러", post_signin_res);
        }

        setIsLoading(false);
    };

    return (
        <view.LoginFormSection>
            <CustomInput
                placeholder={"이메일 계정"}
                on_change={(text) => (user_info.current.account.value = text)}
                name={"account"}
                need_label={false}
                current_value={user_info.current.account}
                is_error={account_error}
                regex={REGEX_EMAIL}
                read_only={is_loading}
            />
            <CustomInput
                type={"password"}
                placeholder={"비밀번호"}
                on_change={(text) => (user_info.current.password.value = text)}
                name={"password"}
                need_label={false}
                current_value={user_info.current.password}
                is_error={password_error}
                regex={REGEX_PASSWORD}
                read_only={is_loading}
            />
            <CustomCheckbox
                title={"로그인 상태 유지"}
                on_checked={(bool) => {
                    user_info.current.keep_login = bool;
                }}
                current_value={user_info.current.keep_login}
            />
            <SubmitButton on_click={validateInputValue} button_text={"로그인"} margin_top={"2.5em"} />
            <view.FindAccount>
                <SpreadItem
                    options={FIND_ACCOUNT_OPTIONS}
                    color={globalTheme.colors.dg2}
                    font_size={globalTheme.font_size.f15}
                />
            </view.FindAccount>
        </view.LoginFormSection>
    );
};

export default LoginForm;

export const useComponentStyle = () => {
    return {
        view: {
            LoginFormSection: styled.div({
                margin: "3em auto 0 auto",
                width: "95%",
                maxWidth: "400px",
                height: "auto",
            }),
            FindAccount: styled.div({
                width: "100%",
                display: "flex",
                justifyContent: "end",
                marginTop: "2em",
            }),
        },
    };
};
